import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Mail, Search, Users, UserX, Phone, CheckCircle2, Clock, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubscribers() {
  const [data, setData] = useState({ items: [], total: 0, pending: 0, confirmed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetch = async () => {
    setLoading(true);
    try {
      const params = searchTerm.trim() ? { q: searchTerm.trim() } : {};
      const res = await axios.get(`${API_URL}/api/admin/subscribers`, { withCredentials: true, params });
      setData(res.data || { items: [], total: 0, pending: 0, confirmed: 0 });
    } catch (e) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetch, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const unsubscribe = async (id) => {
    if (!window.confirm('Mark this subscriber as unsubscribed?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/subscribers/${id}`, { withCredentials: true });
      toast.success('Unsubscribed');
      fetch();
    } catch {
      toast.error('Failed to unsubscribe');
    }
  };

  return (
    <AdminLayout title="Subscribers">
      <div data-testid="admin-subscribers-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="subscribers-title">
            Subscribers ({data.items.length})
          </h1>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard icon={<CheckCircle2 size={20} />} label="Confirmed" value={data.confirmed} tone="green" testId="stat-confirmed" />
          <StatCard icon={<Clock size={20} />} label="Pending Confirmation" value={data.pending} tone="orange" testId="stat-pending" />
          <StatCard icon={<Mail size={20} />} label="Total Subscriptions" value={data.total} tone="blue" testId="stat-total" />
        </div>

        <div className="bg-white p-4 border border-[#E2E8F0] rounded-lg mb-4">
          <div className="relative">
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="subscribers-search"
            />
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : data.items.length === 0 ? (
            <div className="p-8 text-center" data-testid="subscribers-empty">
              <Mail size={48} className="text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-[#475569]">No subscribers yet</p>
              <p className="text-xs text-[#94A3B8] mt-1">
                The Subscribe widget in the footer and Contact page will populate this list.
              </p>
            </div>
          ) : (
            <table className="w-full" data-testid="subscribers-table">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Phone</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Subscribed</th>
                  <th className="text-right px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {data.items.map((s, i) => (
                  <tr key={s.subscriber_id} className="hover:bg-[#F9FAFB]" data-testid={`subscriber-row-${i}`}>
                    <td className="px-6 py-4 font-medium text-[#0A0F1C]">{s.name}</td>
                    <td className="px-6 py-4">
                      <a href={`mailto:${s.email}`} className="text-sm text-[#1D4ED8] hover:underline flex items-center gap-1">
                        <Mail size={12} /> {s.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {s.phone ? (
                        <span className="text-sm text-[#475569] flex items-center gap-1">
                          <Phone size={12} /> {s.phone}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {s.is_active ? (
                        <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-medium">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-[#F1F5F9] text-[#475569] rounded-full font-medium">Unsubscribed</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-[#94A3B8]">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {s.is_active && (
                        <button
                          onClick={() => unsubscribe(s.subscriber_id)}
                          className="text-xs text-[#EF4444] hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 ml-auto"
                          data-testid={`unsubscribe-${i}`}
                        >
                          <UserX size={12} /> Unsubscribe
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, tone, testId }) {
  const tones = {
    green: 'bg-green-50 text-green-700',
    blue: 'bg-[#EFF6FF] text-[#1D4ED8]',
    orange: 'bg-[#FFF7ED] text-[#F97316]',
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone] || tones.blue}`}>{icon}</div>
        <div>
          <div className="text-xs text-[#475569] uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold text-[#0A0F1C]">{value}</div>
        </div>
      </div>
    </div>
  );
}
